import { allAmountsSame, getHighestAmountIndex } from '@afterpay/utils'
import { CheckoutPaymentSchedule, Cohort, Country, FirstPaymentType } from '@afterpaytouch/portal-api'
import { useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import {
  useAppRouter,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useCheckoutStatus,
  useCohort,
  useCountry,
  useGetPreferredPaymentDayQuery,
  useIsBillingAgreementApprovalProcess,
  useIsConsumerLendingOrder,
} from '..'
import { CheckoutStatus, Features, money } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { useMoney } from '../../component/Money/useMoney'

interface SummaryLayoutHook {
  variant: 'E' | 'C'
  showProfileCard: boolean
}

export const useSummaryLayout = (): SummaryLayoutHook => {
  const isConsumerLending = useIsConsumerLendingOrder()
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const country = useCountry()
  const isUS = country === Country.US

  const conditions = [!isConsumerLending, isBronteDesign, isUS]
  const isEligible = conditions.every(Boolean)

  if (!isEligible) {
    return { variant: 'C', showProfileCard: true }
  }

  return { variant: 'E', showProfileCard: false }
}

interface SchedulePreviewText {
  text: string
  isPPDLoading: boolean
}

export const useSchedulePreviewText = (): SchedulePreviewText => {
  const country = useCountry()

  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { locale } = useAppRouter()
  const installments = paymentSchedules.map((schedule) => schedule.amount)
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const shouldUseZeroMoneyInstallment = isNpuf || isBillingAgreementApprovalProcess
  const zeroMoney = money('0.00', installments[0]?.currency)
  const [amountDueToday = zeroMoney] = shouldUseZeroMoneyInstallment ? [zeroMoney] : installments
  const { formatMoney } = useMoney({
    decimalPlaces: 2,
    currency: amountDueToday?.currency,
    locale,
  })

  const isPPDEnabled = useFlag(Features.ENABLE_ALIGN_PAYMENT_DAY)
  const isPPDCountry = country === Country.US || country === Country.AU
  const hasStartedCheckout = useCheckoutStatus() >= CheckoutStatus.Started
  const skipPPDQuery = !isPPDCountry || !isPPDEnabled || isBillingAgreementApprovalProcess || !hasStartedCheckout
  const { data: ppdData, isLoading: isPPDLoading } = useGetPreferredPaymentDayQuery(undefined, { skip: skipPPDQuery })
  const { t } = useTranslation()

  const hasActivePPD = isPPDEnabled && isPPDCountry && ppdData?.isEligible && !!ppdData?.preferredPaymentDay
  const ppdLoading = !skipPPDQuery && isPPDLoading
  const cohort = useCohort()
  const isAPOrNetNew = cohort === Cohort.APOnly || cohort === Cohort.NetNew

  if (isBillingAgreementApprovalProcess) {
    return { text: t('summary:billingApprovalPreviewText', { cadence: 2, context: isAPOrNetNew ? 'estimated' : '' }), isPPDLoading: false }
  }

  if (installments.length === 0) {
    return { text: '', isPPDLoading: false }
  }

  if (allAmountsSame(installments) && !isNpuf) {
    return {
      text: t('summary:sameInstallment', {
        context: hasActivePPD ? 'ppd' : undefined,
        installment: formatMoney(amountDueToday.amount),
      }),
      isPPDLoading: ppdLoading,
    }
  }

  const restOfInstallments = isNpuf ? installments : installments.slice(1)
  const restOfInstallmentsEqual = allAmountsSame(restOfInstallments)
  const idx = getHighestAmountIndex(restOfInstallments)
  const highestAmount = restOfInstallments[idx]

  if (allAmountsSame([amountDueToday, highestAmount])) {
    return {
      text: t('summary:sameInstallment', {
        context: hasActivePPD ? 'ppdUpTo' : 'upTo',
        installment: formatMoney(highestAmount.amount),
      }),
      isPPDLoading: ppdLoading,
    }
  }

  const paymentContext = restOfInstallmentsEqual ? '' : 'upTo'
  const ppdPaymentContext = restOfInstallmentsEqual ? 'ppd' : 'ppdUpTo'

  const context = hasActivePPD ? ppdPaymentContext : paymentContext

  if (isNpuf) {
    return {
      text: t('summary:npuf', {
        context,
        installment: formatMoney(highestAmount.amount),
        amountDueToday: formatMoney(amountDueToday.amount),
      }),
      isPPDLoading: ppdLoading,
    }
  }

  return {
    text: t('summary:puf', {
      context,
      installment: formatMoney(highestAmount.amount),
      amountDueToday: formatMoney(amountDueToday.amount),
    }),
    isPPDLoading: ppdLoading,
  }
}

export const useExperimentPaymentScheduleList = (): CheckoutPaymentSchedule[] => {
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { firstPaymentType } = useCheckoutState()
  const isNpuf = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const zeroMoney = money('0.00', paymentSchedules[0]?.amount?.currency)
  const dateToday = new Date().toDateString()
  const zeroMoneyPaymentSchedule: CheckoutPaymentSchedule = { amount: zeroMoney, installmentDueDate: dateToday }

  return [...(isNpuf ? [zeroMoneyPaymentSchedule] : []), ...paymentSchedules]
}
